import * as React from 'preact/compat'
import cn from 'classnames'
import Modal from '../Modal'
import buttonsCss from '../../../css/buttons.pcss.json'
import commonCss from '../../../css/v9/common.pcss.json'
import Api from '../../api'
import GamePlatformSection from './GamePlatformSection'
import SVG from '../SVG'
import { captureException } from '../../common/helpers'
import { showToast } from '../toast'
import IncludeExcludeFilter from "../IncludeExcludeFilter.svelte";
import SvelteWrapper from "../SvelteWrapper";

const IncludeExcludeFilter_React = SvelteWrapper(IncludeExcludeFilter)

export interface Platform {
  id: number,
  code: string,
  title: string,
  altTitles: string[],
  exclude?: boolean
}

export interface PlatformSection {
  id: number,
  title: string,
  type: 'generation' | 'developer',
  platforms: string[]
}

interface PropTypes {
  platforms: Array<Platform>,
  name: string,
  hidePlatformList?: boolean,
  numberInButton?: boolean,
  useCodeAsValue?: boolean,
  innerHtml?: string,
  buttonClass?: string,
  allowExclusion?: boolean
}

interface StateTypes {
  platforms: Array<Platform>,
  allPlatforms: Array<Platform>,
  sections: Array<PlatformSection>,
  isModalOpen: boolean,
  groupBy: 'generation' | 'developer'
}

const getGroupBy = (): StateTypes['groupBy'] => {
  let groupBy: StateTypes['groupBy']
  if (window.localStorage) {
    groupBy = localStorage.getItem('game-platforms-group-by') as StateTypes['groupBy']
  }
  if (!groupBy) {
    groupBy = 'generation'
  }
  return groupBy
}

export default class GamePlatformsEditor extends React.Component<PropTypes, StateTypes> {

  state: StateTypes = {
    platforms: this.props.platforms,
    allPlatforms: [],
    sections: [],
    isModalOpen: false,
    groupBy: getGroupBy()
  }

  componentDidUpdate(previousProps: Readonly<PropTypes>, previousState: Readonly<StateTypes>) {
    if (previousProps.platforms !== this.props.platforms) {
      this.setState({
        platforms: this.props.platforms
      })
    }
    if (previousState.platforms !== this.state.platforms) {
      window.dispatchEvent(new CustomEvent('platforms-changed', {
        detail: {
          platforms: this.state.platforms.map(p => {
            if (p.exclude) {
              return '-' + p.id
            }
            return p.id
          })
        }
      }))
    }
  }

  componentDidMount() {
    Api.get<{
      platforms: Array<Platform>,
      sections: Array<PlatformSection>
    }>({ url: 'games/platforms' })
      .then(response => {
        this.setState({
          allPlatforms: response.platforms,
          sections: response.sections
        })
        window.addEventListener('platform-add', (event: Event & {
          detail: {
            platform: number
          }
        }) => {
          const platform = this.state.allPlatforms.find(p => p.id === event.detail.platform)
          if (!platform) {
            return
          }
          this.setState({
            platforms: [...this.state.platforms.filter(p => p.id !== platform.id), platform]
          })
        })
        window.addEventListener('platform-remove', (event: Event & {
          detail: {
            platform: number
          }
        }) => {
          this.setState({
            platforms: this.state.platforms.filter(p => p.id !== event.detail.platform)
          })
        })
        window.addEventListener('reset-platforms', () => {
          this.setState({
            platforms: []
          })
        })
      })
      .catch(error => {
        captureException('Game Platforms Editor', error)
        showToast({
          type: 'error',
          text: 'Не удалось загрузить список платформ. Попробуй позже.',
        })
      })
  }

  openModal = () => {
    this.setState({
      isModalOpen: true
    })
  }

  closeModal = () => {
    this.setState({
      isModalOpen: false
    })
  }

  handlePlatformChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget
    const { platforms, allPlatforms } = this.state
    if (checked) {
      const platform = allPlatforms.find(platform => platform.id === parseInt(value))
      this.setState({
        platforms: [...platforms, platform]
      })
    } else {
      this.setState({
        platforms: platforms.filter(platform => platform.id !== parseInt(value))
      })
    }
  }

  handlePlatformIncludeExclude = (platformId: string, add: boolean, exclude: boolean = false) => {
    const { platforms, allPlatforms } = this.state

    if (add) {
      const platform = {
        ...allPlatforms.find(platform => platform.id === parseInt(platformId)),
        exclude
      }
      this.setState({
        platforms: [...platforms.filter(platform => platform.id !== parseInt(platformId)), platform]
      })
    } else {
      this.setState({
        platforms: platforms.filter(platform => platform.id !== parseInt(platformId))
      })
    }
  }

  changeGroupping = (groupBy: 'generation' | 'developer') => () => {
    this.setState({
      groupBy
    })
    if (window.localStorage) {
      localStorage.setItem('game-platforms-group-by', groupBy)
    }
  }

  renderGamePlatformsModal = () => {
    const sections = [];
    this.state.sections.forEach(section => {
      let hasActive = false;
      if (section.type !== this.state.groupBy) {
        return;
      }
      const platforms = section.platforms.map(platform => {
        const platformInfo = this.state.allPlatforms.find(p => p.code === platform)
        const checked = this.state.platforms.find(p => p.code === platform)
        if (checked) {
          hasActive = true;
        }
        if (this.props.allowExclusion) {
          return <IncludeExcludeFilter_React
            label={platformInfo.title}
            value={platformInfo.id}
            status={checked ? (checked.exclude ? -1 : 1) : 0}
            onChange={this.handlePlatformIncludeExclude}
            SvelteContainerOptions={{
              style: 'margin-bottom: 12px'
            }}
          />
        }
        return <styled-checkbox
          name='platforms[]'
          label={platformInfo.title}
          value={platformInfo.id}
          checked={!!checked}
          onChange={this.handlePlatformChange}
          style='margin-bottom: 12px'
        />
      })
      sections.push(<GamePlatformSection
        title={section.title}
        platforms={platforms}
        collapsible={section.title !== 'Актуальные'}
        opened={hasActive}
        type={this.state.groupBy}
      />)
    })

    return <Modal showClose={true} onClose={this.closeModal} enableScroll={true}>
      <h1 style='font-family: Rubik; font-weight: 500; font-size: 22px; margin-bottom: 16px'>Платформы</h1>
      <div class={commonCss['inner-tabs']} style='margin-bottom: 32px'>
        <button
          type='button'
          className={cn({
            [commonCss['inner-tabs__item']]: true,
            [commonCss['inner-tabs__item--active']]: this.state.groupBy === 'generation'
          })}
          onClick={this.changeGroupping('generation')}
        >
          По поколениям
        </button>
        <button
          type='button'
          className={cn({
            [commonCss['inner-tabs__item']]: true,
            [commonCss['inner-tabs__item--active']]: this.state.groupBy === 'developer'
          })}
          onClick={this.changeGroupping('developer')}
        >
          По компаниям
        </button>
      </div>
      {sections}
    </Modal>
  }

  removePlatform = (platform: number) => {
    this.setState({
      platforms: this.state.platforms.filter(p => p.id !== platform)
    })
  }

  render() {
    const { name, numberInButton, innerHtml, hidePlatformList, useCodeAsValue } = this.props
    const { platforms, isModalOpen } = this.state

    const platformsInputs = platforms.map(platform => {
      let value = useCodeAsValue ? platform.code.toLowerCase() : platform.id;
      if (platform.exclude) {
        value = `-${value}`
      }
      return <input
        type='hidden'
        name={`${name}[]`}
        value={value}
      />
    })
    const platformsString = platforms.map(platform => <span style='display: flex; align-items: center'>
      {platform.title}
      <button
        type='button'
        className={cn(buttonsCss['icon'], buttonsCss['icon--low-contrast'])}
        style='--icon-size: 32px'
        onClick={() => this.removePlatform(platform.id)}
      >
        <SVG i='sg-new/cross' adaptive/>
      </button>
    </span>)

    let buttonContent: string | React.JSX.Element = 'Выбрать платформы'
    if (numberInButton && platforms.length > 0) {
      const excludedPlatformsCount = platforms.filter(p => p.exclude).length;
      const includedPlatformsCount = platforms.filter(p => !p.exclude).length
      buttonContent = <>
        Платформы&nbsp;
        {includedPlatformsCount > 0 && <span style='color: var(--green)'>+{includedPlatformsCount}</span>}
        {includedPlatformsCount > 0 && excludedPlatformsCount > 0 &&
          <span style='color: var(--v9-color-text-min-vis)'>/</span>}
        {excludedPlatformsCount > 0 && <span style='color: var(--brand-red)'>-{excludedPlatformsCount}</span>}
      </>
    } else if (innerHtml) {
      buttonContent = innerHtml
    }

    let buttonClass = buttonsCss['low-contrast']
    if (this.props.buttonClass) {
      buttonClass = this.props.buttonClass
    }

    return <>
      {platformsInputs}
      {!hidePlatformList
        && <div style='display: flex; column-gap: 24px; row-gap: 8px; margin-bottom: 16px; flex-wrap: wrap'>
          {platformsString}
        </div>}
      <button className={buttonClass} type='button' onClick={this.openModal}>
        {buttonContent}
      </button>
      {isModalOpen && this.renderGamePlatformsModal()}
    </>
  }
}
